'use client';

import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import clsx from 'clsx';
import { Icon, Typography } from '../common';
import Icons from '@/const/icons';
import { InputText } from '../form';
import { useForm } from 'react-hook-form';

interface ComboboxSearchProps {
	placeholder: string;
	value?: string;
	open: boolean;
	setOpen: (value: boolean) => void;
	handleSearch?: (value: string) => void;
	handleClearAll?: () => void;
	classNameTrigger?: string;
	classNameButton?: string;
	classNamePopoverContent?: string;
	hasValue?: boolean;
	customButton?: React.ReactNode;
}

export const ComboboxSearch = ({
	placeholder,
	value,
	open,
	setOpen,
	handleSearch,
	handleClearAll,
	classNameTrigger,
	classNameButton,
	classNamePopoverContent,
	hasValue,
	customButton,
}: ComboboxSearchProps) => {
	const { register, setValue: setFormValue } = useForm();

	React.useEffect(() => {
		setFormValue('search', value || '');
	}, [value, setFormValue]);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger
				asChild
				className={clsx(
					`p-2 !text-b14 !text-black border-smoke-50 rounded-lg justify-between md:justify-start w-full z-[60] disabled:cursor-not-allowed`,
					classNameTrigger
				)}
			>
				{customButton ? (
					customButton
				) : (
					<Button
						variant="outline"
						aria-expanded={open}
						className={clsx(
							'relative gap-2 min-w-max bg-white',
							classNameButton
						)}
					>
						<Typography
							type="semibold-14"
							className={clsx(
								'!line-clamp-1 flex-1 text-ellipsis overflow-hidden whitespace-normal text-left opacity-60'
							)}
						>
							{placeholder}
						</Typography>
						<ChevronDown
							className={`mt-0.5 h-5 w-5 shrink-0 text-[#0D0616] ${
								open && 'rotate-180'
							}`}
						/>
						{hasValue && (
							<span className="w-[10px] h-[10px] bg-main-blue rounded-full absolute -top-1 -right-1 z-20" />
						)}
					</Button>
				)}
			</PopoverTrigger>
			<PopoverContent
				onOpenAutoFocus={(e) => e.preventDefault()}
				className={clsx(
					'max-h-96 w-[258px] p-0 border-none overflow-y-auto scroll-custom shadow-[0px_2px_5px_0px_#0000003D]',
					classNamePopoverContent
				)}
				align="start"
			>
				{handleClearAll && (
					<Button
						variant={'ghost'}
						className={clsx(
							'font-normal flex justify-start items-center gap-2 h-12 px-4 w-full hover:bg-grey-100 rounded-none'
						)}
						onClick={() => {
							handleClearAll();
							setOpen(false);
						}}
					>
						<Icon src={Icons.broom} className="!w-5 !h-5" />
						<Typography type="regular-14">Clear All</Typography>
					</Button>
				)}
				<div className="bg-white text-black pt-2 pb-4 px-4">
					<InputText
						autoFocus
						name="search"
						register={register}
						placeholder="Search"
						iconLeft={Icons.search}
						className="w-full"
						classNameIcon={'!top-[23%] !w-4 !h-4 text-opacity-black'}
						classNameInput="pl-9 !bg-gray-15 !h-[33px] !m-0 border-none 3xl:!text-sm"
						onChange={({ target }) => {
							handleSearch?.(target.value);
						}}
					/>
				</div>
			</PopoverContent>
		</Popover>
	);
};
